import { useEditorState, type Editor } from '@tiptap/react';
import { Table2 } from 'lucide-react';

import { DropdownMenuWrapper, useDropdownMenuState } from '@/components/ui/tiptap/tiptap-ui-primitives';
import { useIsTableInactive } from '@/components/ui/tiptap/tiptap-ui/table';
import { useTiptapEditor } from '@/hooks/tiptap';
import { isNodeInSchema, shouldShow } from '@/lib/tiptap';

import { ToggleHeaderButton, toggleHeaderOptions, type ToggleHeaderDropdownMenuProps, type ToggleHeaderType } from '.';

function isActive(editor: Editor | null, type: ToggleHeaderType): boolean {
  if (!editor) return false;
  switch (type) {
    case 'columnHeader':
      return editor.isActive('columnHeader');
    case 'rowHeader':
      return editor.isActive('rowHeader');
    case 'cellHeader':
      return editor.isActive('cellHeader');
    default:
      return false;
  }
}

function useIsAnyToggleHeaderActive(editor: Editor | null, types: ToggleHeaderType[]): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => {
        return types.some((type) => ctx.editor?.isActive(type)) || ctx.editor?.isActive('table');
      },
    }) ?? false
  );
}

export function ToggleHeaderDropdownMenu(toggleHeaderDropdownMenuProps: ToggleHeaderDropdownMenuProps) {
  const { editor: providedEditor, types = ['columnHeader', 'rowHeader', 'cellHeader'], onOpenChange, ...props } = toggleHeaderDropdownMenuProps;
  const editor: Editor | null = useTiptapEditor(providedEditor);
  const isDisabled: boolean = useIsTableInactive(editor);
  const isAnyActive: boolean = useIsAnyToggleHeaderActive(editor, types);
  const inSchema: boolean = isNodeInSchema('table', editor);
  const { isOpen, filteredItems, handleOpenChange, activeIcon } = useDropdownMenuState<ToggleHeaderType>(editor, types, toggleHeaderOptions, isActive, <Table2 />);

  const show: boolean = shouldShow([!!editor, inSchema]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenuWrapper
      isOpen={isOpen}
      onOpenChange={(open) => handleOpenChange(open, onOpenChange)}
      ariaLabel="toggle header"
      buttonProps={props}
      activeIcon={activeIcon()}
      isDisabled={isDisabled}
      isActive={isAnyActive}
    >
      <>
        {filteredItems.map((option) => (
          <ToggleHeaderButton key={option.type} editor={editor} type={option.type} />
        ))}
      </>
    </DropdownMenuWrapper>
  );
}
